/** Module keys aligned with backend constants (role_features / features catalog). */
export const MODULE_KEYS = {
  CONTROLS: 'controls',
  SOX_ISSUES: 'sox_issues',
  TEST_EXECUTIONS: 'test_executions',
  RISKS: 'risks',
  PROCESSES: 'processes',
  MATERIALITY: 'materiality',
  REPORTS: 'reports',
  SYSTEMS: 'systems',
  SOF: 'sof',
  EVIDENCE: 'evidence',
  FILES: 'files',
  SOP_SYNTHESIZER: 'sop_synthesizer',
} as const;

export type ModuleKey = (typeof MODULE_KEYS)[keyof typeof MODULE_KEYS];

export type OrgModuleAccess = {
  role: string;
  feature_keys: string[];
  unrestricted: boolean;
  reporting_enabled: boolean;
  /** Widgets may be switched to stored demo data (backend WIDGETS_DEMO_DATA on, not production). */
  widgets_demo_data?: boolean;
  /** True when this org has at least one active non-billing integration (AI providers). */
  has_own_ai_credentials: boolean;
};

/** Roles historically filtered by module access (kept for reference/compat). */
export const MODULE_FILTER_ROLES = [
  'internal_auditor',
  'external_auditor',
  'report_viewer',
] as const;

export const SIDEBAR_MODULE_FILTER_ROLES = MODULE_FILTER_ROLES;
export const ROUTE_MODULE_FILTER_ROLES = MODULE_FILTER_ROLES;

/** True when API says access is restricted — apply feature_keys for any role (incl. org_admin). */
export function shouldApplySidebarModuleFilter(access: OrgModuleAccess | undefined): boolean {
  if (!access || access.unrestricted) return false;
  return true;
}

/** Same as sidebar: restricted access → enforce feature_keys on routes for any role. */
export function shouldApplyRouteModuleFilter(access: OrgModuleAccess | undefined): boolean {
  if (!access || access.unrestricted) return false;
  return true;
}

/** Restricted with zero keys (e.g. org∩role empty) — not the same as unrestricted nil. */
export function hasZeroModuleAccess(access: OrgModuleAccess | undefined): boolean {
  if (!access || access.unrestricted) return false;
  return (access.feature_keys?.length ?? 0) === 0;
}

export function isModuleTabVisible(
  tab: { moduleKey?: string; moduleKeys?: string[] },
  access: OrgModuleAccess | undefined
): boolean {
  if (!shouldApplySidebarModuleFilter(access)) return true;
  const keys = tab.moduleKeys ?? (tab.moduleKey ? [tab.moduleKey] : []);
  // No moduleKey = not module-gated; keep visible for restricted roles.
  if (keys.length === 0) return true;
  const allowed = access?.feature_keys ?? [];
  return keys.some((k) => allowed.includes(k));
}

export function filterTabsByModuleAccess<T extends { moduleKey?: string; moduleKeys?: string[] }>(
  tabs: T[],
  access: OrgModuleAccess | undefined
): T[] {
  if (!shouldApplySidebarModuleFilter(access)) return tabs;
  return tabs.filter((tab) => isModuleTabVisible(tab, access));
}

/** True when the user may use actions/APIs for a single module (same rules as sidebar). */
export function hasModuleAccess(
  access: OrgModuleAccess | undefined,
  moduleKey: ModuleKey
): boolean {
  return isModuleTabVisible({ moduleKey }, access);
}

/** True when the user has every listed module (same rules as sidebar). */
export function hasAllModuleAccess(
  access: OrgModuleAccess | undefined,
  moduleKeys: ModuleKey[]
): boolean {
  if (moduleKeys.length === 0) return true;
  return moduleKeys.every((key) => hasModuleAccess(access, key));
}
